import { Injectable } from '@angular/core';
import { validate, v4 as uuid } from 'uuid';
import cloneDeep from 'lodash.clonedeep';
import {
  Observable,
  take,
  withLatestFrom,
  mergeMap,
  of,
  filter,
  combineLatest,
  catchError,
  EMPTY,
  map,
} from 'rxjs';

import {
  CreateDprDetailsOfflineService,
  DprAccumulationOfflineService,
  DprDetailsOfflineRevisionService,
  DprDetailsOfflineService,
  DprDetailsSyncModelBuilderService,
} from '@dpr-details/services/sync';
import {
  DprApprover,
  DprDetails,
  DprOnlineApproval,
  DprDetailsSync,
} from '@shared/models/dpr';
import { SyncService } from '@shared/services/grid';
import { OfflineFacade, UserDetailsFacade } from '@shared/state/facades';
import { ApproveStatus, ApproveStatusPriority } from '@shared/enums/dpr';
import { DprCard } from '@dpr/models';
import {
  WriteDprPersonnel,
  WriteDprStatusTracker,
  WriteDprTableOfColumns,
} from '@dpr-details/models/sync';

@Injectable()
export class DprOfflineService {
  constructor(
    private offlineFacade: OfflineFacade,
    private userDetailsFacade: UserDetailsFacade,
    private dprDetailsOfflineService: DprDetailsOfflineService,
    private dprDetailsOfflineRevisionService: DprDetailsOfflineRevisionService,
    private dprDetailsSyncModelBuilderService: DprDetailsSyncModelBuilderService,
    private createDprDetailsOfflineService: CreateDprDetailsOfflineService,
    private dprAccumulationOfflineService: DprAccumulationOfflineService,
    private syncService: SyncService
  ) {}

  lockDpr(
    dprDetailId: number | string,
    lockDate: string
  ): Observable<DprDetails> {
    return this.dprDetailsOfflineService.getOrCreateChanges(dprDetailId).pipe(
      take(1),
      withLatestFrom(this.offlineFacade.getDprDetails(dprDetailId)),
      filter(([_, dprDetails]) => !!dprDetails),
      mergeMap(([dprDetailsChanges, dprDetails]) => {
        this.offlineFacade.setDprDetailsLockedDate(
          dprDetails.id,
          new Date(lockDate)
        );

        dprDetailsChanges.lockedDate = new Date(lockDate);
        dprDetailsChanges.isSignDprDetailsUploaded = false;

        this.offlineFacade.updateDprDetailsOfflineChanges(dprDetailsChanges);

        return of({
          ...dprDetails,
          lockedDate: dprDetailsChanges.lockedDate,
          isSignDprDetailsUploaded: dprDetailsChanges.isSignDprDetailsUploaded,
        });
      }),
      catchError((error) => {
        console.error('Error locking the DPR', error);
        return EMPTY;
      })
    );
  }

  unlockDpr(dprDetailId: number | string): Observable<number | string> {
    return this.dprDetailsOfflineRevisionService
      .createNewRevision(dprDetailId)
      .pipe(
        take(1),
        mergeMap((newDprDetails) => {
          this.offlineFacade.setDprDetailsNewRevision(
            dprDetailId,
            newDprDetails
          );
          return combineLatest([
            this.dprDetailsOfflineRevisionService.createChanges(
              newDprDetails,
              this.dprDetailsSyncModelBuilderService.forDprDetailId(
                newDprDetails.id
              )
            ),
            of(newDprDetails),
          ]);
        }),
        mergeMap(([dprDetailsChanges, newDprDetails]) => {
          this.offlineFacade.updateDprDetailsOfflineChanges(dprDetailsChanges);

          return of(newDprDetails.id);
        }),
        catchError((error) => {
          console.error('Error unlocking the DPR', error);
          return EMPTY;
        })
      );
  }

  createDpr(subprojectId: number, date: Date): Observable<DprCard> {
    return this.createDprDetailsOfflineService
      .createDprDetails(subprojectId, date)
      .pipe(
        mergeMap((dprDetails) => {
          this.offlineFacade.addDprDetails(dprDetails);

          return combineLatest([
            of(dprDetails),
            this.dprDetailsOfflineService.getOrCreateChanges(dprDetails.id),
            this.userDetailsFacade.query.accountId$,
          ]);
        }),
        take(1),
        mergeMap(([dprDetails, dprDetailsChanges, accountId]) => {
          this.offlineFacade.updateDprDetailsOfflineChanges({
            ...dprDetailsChanges,
            ...this.copyDataFromPreviousDprChanges(dprDetails, accountId),
          });

          // The offline date picker only offers dates after the last DPR, so no
          // DPR is cached after this one today; this guards a date between two.
          return this.dprAccumulationOfflineService
            .updateDependentPreviousValues(dprDetails)
            .pipe(map(() => this.convertToDPRCard(dprDetails)));
        })
      );
  }

  convertToDPRCard(dprDetails: DprDetails): DprCard {
    return !!dprDetails
      ? {
          id: dprDetails.dprId,
          displayId: dprDetails.dprDisplayId,
          subprojectId: dprDetails.dprSubprojectId,
          dateTime: dprDetails.dprDateTime,
          dprDetailId: dprDetails.id,
          dprDetailRevision: dprDetails.revision,
          dprDetailLockedDate: dprDetails.lockedDate,
          dprDetailsFirstWarning: dprDetails.firstWarning,
          dprDetailsWarningStatusTab: dprDetails.warningStatusTab,
          dprDetailsApproveStatus: dprDetails.approveStatus,
          dprApprovers: dprDetails.dprApprovers,
          dprOnlineApprovals: dprDetails.dprOnlineApprovals,
          isSignDprDetailsUploaded: dprDetails.isSignDprDetailsUploaded,
          signedReportExternalUrl: dprDetails.signedReportExternalUrl,
          signedReportName: dprDetails.signedReportName,
          signedReportFileType: dprDetails.signedReportFileType,
          hasImagesToPrint: false,
          hasActiveSignRequest: false,
          isFirstDpr: false,
        }
      : null;
  }

  createDprApprover(model: DprApprover): Observable<DprApprover> {
    return this.dprDetailsOfflineService
      .getOrCreateChanges(model.dprDetailId)
      .pipe(
        take(1),
        mergeMap((dprDetailsChanges) => {
          const changes = cloneDeep(dprDetailsChanges);
          changes.syncDprApprovers =
            changes.syncDprApprovers || this.syncService.getDefaultSyncModel();

          model.id = uuid();
          changes.syncDprApprovers.addedList.push(model);

          this.offlineFacade.updateDprDetailsOfflineChanges(changes);

          return of(model);
        }),
        catchError((error) => {
          console.error('Error creating DPR approver', error);
          return EMPTY;
        })
      );
  }

  updateDprApprover(model: DprApprover): Observable<DprApprover> {
    return this.dprDetailsOfflineService
      .getOrCreateChanges(model.dprDetailId)
      .pipe(
        take(1),
        mergeMap((dprDetailsChanges) => {
          const changes = cloneDeep(dprDetailsChanges);
          changes.syncDprApprovers =
            changes.syncDprApprovers || this.syncService.getDefaultSyncModel();

          let dprApprover: DprApprover;
          if (validate(model.id.toString())) {
            dprApprover = changes.syncDprApprovers.addedList?.find(
              // double equal needed, because in offline the ids are sent as strings
              // eslint-disable-next-line eqeqeq
              (x) => x.id == model.id
            );

            if (!dprApprover) {
              throw new Error('Specified approver item could not be found.');
            }

            dprApprover.name = model.name;
          } else {
            dprApprover = changes.syncDprApprovers.updatedList?.find(
              // double equal needed, because in offline the ids are sent as strings
              // eslint-disable-next-line eqeqeq
              (x) => x.id == model.id
            );

            if (dprApprover) {
              dprApprover.name = model.name;
            } else {
              changes.syncDprApprovers.updatedList.push(model);
              dprApprover = model;
            }
          }

          this.offlineFacade.updateDprDetailsOfflineChanges(changes);

          return of(dprApprover);
        }),
        catchError((error) => {
          console.error('Error updating DPR approver', error);
          return EMPTY;
        })
      );
  }

  deleteDprApprover(
    dprApproverId: number | string,
    dprDetailId: number | string
  ): Observable<void> {
    return this.dprDetailsOfflineService.getOrCreateChanges(dprDetailId).pipe(
      take(1),
      mergeMap((dprDetailsChanges) => {
        const changes = cloneDeep(dprDetailsChanges);
        changes.syncDprApprovers =
          changes.syncDprApprovers || this.syncService.getDefaultSyncModel();

        let indexOf: number;
        if (validate(dprApproverId.toString())) {
          indexOf = changes.syncDprApprovers.addedList?.findIndex(
            // double equal needed, because in offline the ids are sent as strings
            // eslint-disable-next-line eqeqeq
            (x) => x.id == dprApproverId
          );
          if (indexOf !== -1) {
            changes.syncDprApprovers.addedList.splice(indexOf, 1);
          }
        } else {
          indexOf = changes.syncDprApprovers.updatedList.findIndex(
            // double equal needed, because in offline the ids are sent as strings
            // eslint-disable-next-line eqeqeq
            (x) => x.id == dprApproverId
          );
          if (indexOf !== -1) {
            changes.syncDprApprovers.updatedList.splice(indexOf, 1);
          }

          changes.syncDprApprovers.deletedIds.push(dprApproverId);
        }

        return of(this.offlineFacade.updateDprDetailsOfflineChanges(changes));
      }),
      catchError((error) => {
        console.error('Error deleting DPR approver', error);
        return EMPTY;
      })
    );
  }

  saveDprOnlineApproval(model: DprOnlineApproval): Observable<{
    dprOnlineApproval: DprOnlineApproval;
    dprDetail: DprDetails;
  }> {
    return this.dprDetailsOfflineService
      .getOrCreateChanges(model.dprDetailId)
      .pipe(
        mergeMap((dprDetailsChanges) => {
          const changes: DprDetailsSync = cloneDeep(dprDetailsChanges);
          model.id = model.id ?? uuid();
          changes.dprOnlineApproval = model;

          this.offlineFacade.updateDprDetailsOfflineChanges(changes);

          // needed for the overall status computation
          this.offlineFacade.updateDprOnlineApproval(model.dprDetailId, model);

          return combineLatest([
            of(model),
            this.updateDprDetailApproveStatus(model),
          ]);
        }),
        mergeMap(([dprOnlineApproval, dprDetail]) => {
          return of({
            dprOnlineApproval,
            dprDetail,
          });
        }),
        catchError((error) => {
          console.error('Error saving DPR online approval', error);
          return EMPTY;
        })
      );
  }

  //#region Private methods

  private updateDprDetailApproveStatus(
    dprOnlineApproval: DprOnlineApproval
  ): Observable<DprDetails> {
    return combineLatest([
      this.offlineFacade.getDprDetails(dprOnlineApproval.dprDetailId),
      this.userDetailsFacade.query.projectSetup$,
    ]).pipe(
      take(1),
      filter(
        ([dprDetails, projectSetup]) =>
          !!dprDetails && !!projectSetup?.onlineApproverList
      ),
      mergeMap(([dprDetails, projectSetup]) => {
        let allAccepted = true;
        let aggregateApproveStatus = ApproveStatus.NotApproved;
        const dprDetail: DprDetails = cloneDeep(dprDetails);

        projectSetup.onlineApproverList.forEach((onlineApprover) => {
          const approval = dprDetails.dprOnlineApprovals?.find(
            (x) => x.accountId === onlineApprover.accountId
          );

          if (!approval) {
            if (onlineApprover.canApprove) {
              allAccepted = false;
            }
            return;
          }

          if (approval.approveStatus !== ApproveStatus.Accepted) {
            allAccepted = false;
          }

          if (
            projectSetup.onlineApproverList?.filter((x) => x.canApprove)
              .length === 1
          ) {
            aggregateApproveStatus = approval.approveStatus;
          }

          if (
            approval.approveStatus &&
            ApproveStatusPriority.indexOf(aggregateApproveStatus) >
              ApproveStatusPriority.indexOf(approval.approveStatus)
          ) {
            aggregateApproveStatus = approval.approveStatus;
          }
        });

        dprDetail.approveStatus = allAccepted
          ? ApproveStatus.Accepted
          : aggregateApproveStatus;

        this.offlineFacade.updateDprApproveStatus(
          dprDetails.id,
          dprDetail.approveStatus
        );

        return of(dprDetail);
      })
    );
  }

  /**
   * The new DPR already holds what it copied from the DPR before it (approvers,
   * Status Tracker auto-copy, worked hours, Table of Columns anchor rows); those
   * rows are sent as added rows, because a DPR created on the device gets nothing
   * copied when it reaches the API.
   */
  private copyDataFromPreviousDprChanges(
    currentDprDetails: DprDetails,
    accountId: string
  ): DprDetailsSync {
    return {
      id: currentDprDetails.id,
      dprId: currentDprDetails.dprId,
      dprDisplayId: currentDprDetails.dprDisplayId,
      dprSubprojectId: currentDprDetails.dprSubprojectId,
      accountId,
      dprDateTime: currentDprDetails.dprDateTime,
      revision: currentDprDetails.revision,
      isSignDprDetailsUploaded: currentDprDetails.isSignDprDetailsUploaded,
      syncDprApprovers: {
        ...this.syncService.getDefaultSyncModel(),
        addedList: [...(currentDprDetails.dprApprovers ?? [])],
      },
      dprStatusTracker: this.buildCopiedStatusTrackerChanges(currentDprDetails),
      dprPersonnel: this.buildCopiedPersonnelChanges(currentDprDetails),
      dprTableOfColumns:
        this.buildCopiedTableOfColumnsChanges(currentDprDetails),
    };
  }

  /**
   * Queues the anchor rows the new DPR got from the previous one, as added rows. The
   * API copies nothing into a DPR created offline, so without this the rows would
   * never reach the server. Queuing them also keeps them on the DPR: an offline save
   * of the table rebuilds its offline-created rows from the queue, so a row that isn't
   * queued would disappear at the first save.
   *
   * Only tables with rows are queued. The new DPR can also hold empty table entries
   * that only carry the carry-over starting values, and those are not sent.
   */
  private buildCopiedTableOfColumnsChanges(
    currentDprDetails: DprDetails
  ): WriteDprTableOfColumns[] {
    return (currentDprDetails.dprTableOfColumns ?? [])
      .filter(
        (dprTableOfColumn) => dprTableOfColumn.dprTableOfColumnsDetails?.length
      )
      .map(
        (dprTableOfColumn) =>
          this.dprDetailsSyncModelBuilderService.forDprTableOfColumns(
            currentDprDetails.dprSubprojectId,
            currentDprDetails.id,
            dprTableOfColumn.id,
            dprTableOfColumn.tableOfColumnId,
            {
              ...this.syncService.getDefaultSyncModel(),
              addedList: cloneDeep(dprTableOfColumn.dprTableOfColumnsDetails),
            },
            this.syncService.getDefaultSyncModel(),
            null
          ).dprTableOfColumn
      );
  }

  /**
   * The create already left out the derived cells and gave each cell the id it is
   * queued under, so the cells are queued as the new DPR holds them.
   */
  private buildCopiedStatusTrackerChanges(
    currentDprDetails: DprDetails
  ): WriteDprStatusTracker {
    return {
      id: currentDprDetails.dprStatusTracker.id,
      dprDetailId: currentDprDetails.id,
      syncDprStatusTrackerDetails: {
        addedList: cloneDeep(
          currentDprDetails.dprStatusTracker.dprStatusTrackerDetails ?? []
        ),
        updatedList: [],
        deletedIds: [],
      },
    };
  }

  private buildCopiedPersonnelChanges(
    currentDprDetails: DprDetails
  ): WriteDprPersonnel {
    return {
      id: currentDprDetails.dprPersonnel.id,
      dprDetailId: currentDprDetails.id,
      comment: currentDprDetails.dprPersonnel.comment,
      syncDprPersonnelDetails: {
        addedList: (
          currentDprDetails.dprPersonnel.dprPersonnelDetails ?? []
        ).filter((x) => x.workedHours != null),
        updatedList: [],
        deletedIds: [],
      },
      syncDprPersonnelAttachments: {
        addedList: [],
        updatedList: [],
        deletedIds: [],
      },
    };
  }

  //#endregion
}
